import { backgroundOrange, blackOrange, darkOrange, gold, green, orange, vanilla } from '@/theme/colors'
import styled from 'styled-components'

export const DetailPageContainer = styled.div`
    background-color: ${backgroundOrange};
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

export const LoadingContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: ${backgroundOrange};
    height: 100%;
`

export const ErrorContainer = styled.div`
    padding: 2rem;
    text-align: center;
    color: red;
`

export const ScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;

    @media (max-width: 600px) {
        padding: 1rem 0.875rem;
    }
`

export const DetailCard = styled.div`
    background: white;
    border-radius: 16px;
    border: 1px solid #f0e0d6;
    max-width: 720px;
    margin: 0 auto;
    overflow: hidden;
`

export const DetailHeader = styled.div`
    padding: 1.5rem 1.5rem 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        padding: 1.125rem 1rem 0;
        gap: 0.75rem;
    }
`

export const DetailTitle = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #262626;
    line-height: 1.3;
    word-break: break-word;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        font-size: 1.25rem;
    }
`

export const ManualPill = styled.span`
    font-size: 0.6875rem;
    font-weight: 600;
    color: white;
    background: ${blackOrange}cc;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    white-space: nowrap;
`

export const ActionButtons = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
`

export const IconButton = styled.button<{ $variant?: 'default' | 'danger' }>`
    background: none;
    border: 1px solid ${({ $variant }) => ($variant === 'danger' ? '#fca5a5' : '#e5e7eb')};
    border-radius: 8px;
    padding: 0.5rem;
    cursor: pointer;
    color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : '#6b7280')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
        border-color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : orange)};
        color: ${({ $variant }) => ($variant === 'danger' ? '#dc2626' : orange)};
        background: ${({ $variant }) => ($variant === 'danger' ? '#fef2f2' : '#fef3e2')};
    }
`

export const DetailBody = styled.div`
    padding: 1.25rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (max-width: 600px) {
        padding: 1rem 1rem 1.25rem;
        gap: 1.125rem;
    }
`

export const SectionLabel = styled.h3`
    font-size: 0.8125rem;
    font-weight: 700;
    color: ${darkOrange};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 0.75rem;
`

export const LegsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

export const LayoverBanner = styled.div`
    text-align: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${gold};
    padding: 0.375rem 0;
`

/** $interactive=false renders the read-only (public) leg: no pointer, no hover. */
export const LegCard = styled.div<{ $isManual?: boolean; $interactive?: boolean }>`
    padding: 1rem 1.125rem;
    background: ${vanilla};
    border-radius: 12px;
    border: 1px solid ${({ $isManual }) => ($isManual ? `${blackOrange}22` : 'transparent')};
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    cursor: ${({ $interactive = true }) => ($interactive ? 'pointer' : 'default')};
    transition: all 0.15s;

    &:hover {
        border-color: ${({ $interactive = true, $isManual }) =>
            $interactive ? `${orange}40` : $isManual ? `${blackOrange}22` : 'transparent'};
        background: ${({ $interactive = true }) => ($interactive ? '#fef3e2' : vanilla)};
    }
`

export const LegHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.5rem;
`

export const LegCode = styled.span`
    font-size: 1rem;
    font-weight: 700;
    color: #262626;
`

export const LegRoute = styled.span`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${blackOrange};
`

export const LegRouteBlock = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0 0.25rem;
`

export const LegRouteEndpoint = styled.div<{ $align: 'start' | 'end' }>`
    display: flex;
    flex-direction: column;
    align-items: ${({ $align }) => ($align === 'start' ? 'flex-start' : 'flex-end')};
    text-align: ${({ $align }) => ($align === 'start' ? 'left' : 'right')};
    gap: 0.25rem;
    min-width: 0;
`

export const LegRouteAirport = styled.span`
    font-size: 1.5rem;
    font-weight: 800;
    color: #262626;
    letter-spacing: 0.02em;
    line-height: 1;

    @media (max-width: 600px) {
        font-size: 1.25rem;
    }
`

export const LegRouteArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${orange};
    position: relative;
    min-width: 64px;
    padding: 0 0.25rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 0;
        border-top: 1.5px dashed ${orange}66;
        min-width: 14px;
    }

    svg {
        margin: 0 0.5rem;
        flex-shrink: 0;
    }

    @media (max-width: 600px) {
        min-width: 40px;

        &::before,
        &::after {
            min-width: 8px;
        }
    }
`

export const LegTimeGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    padding: 0.625rem 0;
    border-top: 1px solid #f5ebe5;
    border-bottom: 1px solid #f5ebe5;
`

export const LegTimeBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
`

export const LegTimeLabel = styled.span`
    font-size: 0.625rem;
    font-weight: 700;
    color: ${gold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`

export const LegTimeValue = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
    color: #262626;
`

export const LegMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.75rem;
    color: #6b7280;
`

export const LegPassengerRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.375rem;
`

export const LegEditHint = styled.span`
    font-size: 0.6875rem;
    color: ${darkOrange};
    font-weight: 600;
    margin-left: auto;
`

export const AvatarStack = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

export const Avatar = styled.div<{ $index: number }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${orange};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    font-weight: 600;
    flex-shrink: 0;
    border: 2px solid white;
    margin-left: ${({ $index }) => ($index > 0 ? '-6px' : '0')};
    z-index: ${({ $index }) => 10 - $index};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

export const OverflowAvatar = styled(Avatar)`
    background: ${gold};
    font-size: 0.5rem;
`

export const PassengerCountText = styled.span`
    font-size: 0.6875rem;
    color: #6b7280;
    font-weight: 500;
    white-space: nowrap;
`

export const YouPill = styled.span`
    font-size: 0.625rem;
    font-weight: 600;
    color: ${green};
    background: #e8f5e9;
    padding: 0.125rem 0.5rem;
    border-radius: 999px;
`

export const LinksList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

export const LinkItem = styled.a`
    font-size: 0.875rem;
    color: ${orange};
    text-decoration: none;
    padding: 0.625rem 0.75rem;
    background: #fafafa;
    border-radius: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: background 0.15s;

    &:hover {
        background: #fef3e2;
        text-decoration: underline;
    }
`

export const AttachmentButton = styled.button`
    font-size: 0.875rem;
    color: ${orange};
    font-weight: 500;
    padding: 0.625rem 0.75rem;
    background: #fafafa;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    width: 100%;
    transition: background 0.15s;

    &:hover {
        background: #fef3e2;
    }
`

export const NotesContent = styled.div`
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    padding: 0.75rem;
    background: #fafafa;
    border-radius: 8px;
`

export const AddLegCallout = styled.button`
    background: none;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    color: ${gold};
    cursor: pointer;
    padding: 0.625rem;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.15s;

    &:hover {
        border-color: ${orange};
        color: ${orange};
        background: #fef3e2;
    }
`

export const MetaSection = styled.div`
    font-size: 0.6875rem;
    color: #9ca3af;
    border-top: 1px solid #f0e0d6;
    padding-top: 1rem;
`

export const NotFoundMessage = styled.div`
    padding: 2rem;
    text-align: center;
    color: #6b7280;
`

export const LegCountBadge = styled.span`
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
`
